﻿/* ==========================================================================
   IRON FIT — the cutscene between a machine screen and its page

   Two halves of one move, either side of a page load.

   OUT, on the homepage: the machine you are looking at lifts off its screen,
   travels to the middle, turns half a turn and comes towards you until it
   fills the frame and the frame goes black.

   IN, on the machine's own page: the black is already there when the page
   paints, and it pulls back to reveal the page behind it.

   WHY IT IS TWO HALVES. A navigation ends one document and starts another;
   nothing can animate across that. Cross-document view transitions would,
   but they are Chrome-only, so instead the first half hands the second a
   note in sessionStorage and the second half plays only if it finds one.
   Arrive at a machine page any other way — a link, a search result, the back
   button — and there is no note, so there is no black screen to sit through.

   WHAT IT ANIMATES. The model itself where there is one: the camera is swung
   180 degrees around it and pulled in close, so what turns is the object,
   lit and shaded, rather than a picture of it. Nothing is cloned - cloning a
   model-viewer gives you an empty box, because it is a canvas.

   The model is not always there. It arrives as you scroll near a screen, so
   a visitor who taps quickly may never have had one. In that case the cut-out
   photograph flies instead - every screen carries it at full size, which is
   what the reel shows before the model lands.

   The whole thing is skippable — a tap gets on with it — and it does not run
   at all for anyone who has asked for reduced motion, because a full-screen
   zoom is exactly the kind of movement that request is about.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'ifz-cut';
  var OUT_MS = 620;
  var IN_MS = 520;

  function reduced() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ----------------------------------------------------------------------
     THE WAY IN — played on the machine page, if we were sent here
     ---------------------------------------------------------------------- */
  function playIn() {
    var note;
    try { note = sessionStorage.getItem(KEY); } catch (e) { return; }
    if (!note) return;
    try { sessionStorage.removeItem(KEY); } catch (e) {}
    if (reduced()) return;

    var veil = document.createElement('div');
    veil.className = 'ifzcut ifzcut--in';
    document.body.appendChild(veil);

    function drop() {
      if (veil.parentNode) veil.parentNode.removeChild(veil);
    }

    /* The teardown is a plain timer and it is set FIRST, before anything that
       could fail to happen. It used to live inside the frame callbacks below,
       which is fine until those callbacks do not run — and they do not run in
       a backgrounded tab, which is exactly what a tab is while it loads in
       the background. The failure was a black screen that never lifted. Now
       the worst case is the black disappearing without animating. */
    setTimeout(drop, IN_MS + 240);

    /* Reading offsetHeight commits the from-state synchronously, so the
       transition below is guaranteed to run. rAF was doing this job and does
       not fire in a throttled tab. */
    void veil.offsetHeight;
    veil.classList.add('is-out');
  }

  /* ----------------------------------------------------------------------
     THE WAY OUT — played on the homepage, on the way to a machine page
     ---------------------------------------------------------------------- */
  /* ----------------------------------------------------------------------
     THE WAY OUT, when the machine on screen is the real model

     The half turn is done by the model, not to a picture of it: the camera
     is swung 180 degrees around it and pulled in close, and model-viewer
     glides between the old orbit and the new one on its own. Nothing is
     cloned, so what turns is the object itself, lit and shaded, and it
     carries on turning while the black comes up over it.

     auto-rotate has to go first or it fights the camera move and the machine
     wobbles instead of turning.
     ---------------------------------------------------------------------- */
  function turnModel(mv, panel) {
    try {
      mv.removeAttribute('auto-rotate');

      var o = mv.getCameraOrbit ? mv.getCameraOrbit() : null;
      var theta = o ? (o.theta * 180 / Math.PI) : 0;
      var phi = o ? (o.phi * 180 / Math.PI) : 76;

      /* the glide. Left at its default the camera snaps across in about a
         tenth of a second, which reads as a cut rather than a turn. */
      mv.interpolationDecay = 240;

      /* The ATTRIBUTE, not the property. ifz-reel.js turns these machines by
         writing camera-orbit as an attribute on every scroll frame, so the
         attribute is what this model is actually steered by — a property set
         is overwritten by the next frame that scrolls. Setting the same lever
         the reel uses means the turn survives. */
      mv.setAttribute('camera-orbit',
        (theta + 180).toFixed(1) + 'deg ' + phi.toFixed(1) + 'deg 58%');
    } catch (e) { /* an older model-viewer: the zoom below still plays */ }

    var stage = panel.querySelector('.rp__stage') || mv.parentElement;
    if (!stage) return;
    stage.style.transformOrigin = '50% 50%';
    stage.style.transition = 'transform ' + OUT_MS + 'ms cubic-bezier(.55, 0, .85, .5)';
    return stage;
  }

  function playOutModel(mv, panel, href) {
    var sent = false;
    function leave() { if (!sent) { sent = true; window.location.href = href; } }
    var failsafe = setTimeout(leave, OUT_MS + 400);

    try { sessionStorage.setItem(KEY, '1'); } catch (e) {}

    var layer = document.createElement('div');
    layer.className = 'ifzcut ifzcut--out';
    document.body.appendChild(layer);
    layer.addEventListener('click', function () { clearTimeout(failsafe); leave(); });

    var stage = turnModel(mv, panel);

    /* A forced reflow, not two animation frames. rAF does not fire in a
       backgrounded or throttled tab, and when it did not the black never
       came up and the stage never moved - the cutscene silently became a
       plain navigation. Reading offsetHeight flushes style and layout
       synchronously, so the from-state is committed and the transition is
       guaranteed to start on the very next frame. */
    void layer.offsetHeight;
    layer.classList.add('is-on');
    if (stage) { void stage.offsetHeight; stage.style.transform = 'scale(2.1)'; }

    setTimeout(function () { clearTimeout(failsafe); leave(); }, OUT_MS);
  }

  function playOut(shot, href) {
    var go = function () { window.location.href = href; };

    /* Belt and braces: if anything below throws or a frame is dropped, the
       navigation still happens. A cutscene that eats the tap is worse than
       no cutscene. */
    var sent = false;
    function leave() { if (!sent) { sent = true; go(); } }
    var failsafe = setTimeout(leave, OUT_MS + 400);

    try { sessionStorage.setItem(KEY, '1'); } catch (e) {}

    var box = shot.getBoundingClientRect();

    var layer = document.createElement('div');
    layer.className = 'ifzcut ifzcut--out';

    var fly = document.createElement('img');
    fly.className = 'ifzcut__fly';
    fly.src = shot.currentSrc || shot.src;
    fly.alt = '';
    /* Starts exactly where the real one is, so the first frame is
       indistinguishable from the page it is leaving. */
    fly.style.left = box.left + 'px';
    fly.style.top = box.top + 'px';
    fly.style.width = box.width + 'px';
    fly.style.height = box.height + 'px';

    layer.appendChild(fly);
    document.body.appendChild(layer);

    /* the distance from where it stands to the middle of the screen */
    var dx = (window.innerWidth / 2) - (box.left + box.width / 2);
    var dy = (window.innerHeight / 2) - (box.top + box.height / 2);

    void layer.offsetHeight;                          /* commit the from-state */
    layer.classList.add('is-on');                     /* the black comes up */
    fly.style.transform =
      'translate(' + dx + 'px,' + dy + 'px) scale(2.3) rotateY(180deg)';
    fly.style.opacity = '0';

    layer.addEventListener('click', function () {      /* skip */
      clearTimeout(failsafe);
      leave();
    });

    setTimeout(function () {
      clearTimeout(failsafe);
      leave();
    }, OUT_MS);
  }

  /* ----------------------------------------------------------------------
     WIRING
     ---------------------------------------------------------------------- */
  function wire() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a.rbtn--gold');
      if (!a) return;

      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      /* let a new tab, a download or a modified click behave normally */
      if (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey ||
          e.altKey || e.button !== 0) return;

      var panel = a.closest('.rp');
      if (!panel) return;
      if (reduced()) return;                                 /* plain navigation */

      /* The model if it is there and ready, the cut-out if it is not. Which
         one is present depends on how far the visitor has scrolled and how
         the download went, so both paths have to work. */
      var mv = panel.querySelector('model-viewer');
      if (mv && mv.loaded) {
        e.preventDefault();
        playOutModel(mv, panel, href);
        return;
      }

      var shot = panel.querySelector('.rp__shot');
      if (!shot || !(shot.currentSrc || shot.src)) return;   /* nothing to fly */

      e.preventDefault();
      playOut(shot, href);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { playIn(); wire(); });
  } else {
    playIn();
    wire();
  }
})();
